/**
 * The DOM overlay: HUD, start screen, pause, game over.
 *
 * `mountUI()` builds the whole overlay, injects the design tokens as CSS custom
 * properties, subscribes to the game bus and installs the keyboard shortcuts.
 * It never touches game state — every action goes out through the handlers the
 * caller supplies, and every readout comes in from a `GameSnapshot`.
 *
 *   const ui = mountUI({
 *     handlers: {
 *       onStart:    () => engine.start(),
 *       onPlayPile: (pile) => engine.playOn(pile),
 *       onUndo:     () => engine.undo(),
 *       onPause:    (paused) => { clock.running = !paused; },
 *       onRestart:  () => engine.start(),
 *     },
 *   });
 *
 * With the default `autoBind`, the overlay drives itself from the bus:
 * `game:start` shows the HUD, `tick` runs the clock, `score:change` counts the
 * score up, `game:over` plays the breakdown. Turn it off and call `sync()`,
 * `showGame()` and `showGameOver()` by hand.
 */

import './ui.css';
import './parity.css';
import './lifted.css';

import { bus as sharedBus, type EventBus } from '@/core/bus';
import { cssPalette, rankPalette, elevation, motion, type as typeTokens } from '@/core/tokens';
import { ROUND_SECONDS, type FinalScore, type GameSnapshot, type Resolution } from '@/game/types';
import type {
  ChallengeCard,
  ChallengeRecord,
  ChallengeView,
  OpenChallenge,
  Finance,
  Profile,
  QueueTier,
  RankView,
} from '@/net/api';
import { userGames, userStreaks } from '@/net/api';
import { Hud, el, formatScore } from '@/ui/hud';
import { NotifyHost } from '@/ui/notify-host';
import { StartScreen } from '@/ui/home';
import { GameOverScreen, gameChrome } from '@/ui/results';
import { PauseScreen, type QualityChoice } from '@/ui/settings';
import { ChestStage } from '@/chest/chest-stage';
import { LeaderboardScreen } from '@/ui/leaderboard';
import { HowItWorksScreen } from '@/ui/guide-page';
import { SettingsScreen } from '@/ui/settings-page';
import type { FeeBounds } from '@/ui/fee';
import { mountSignupLayer } from '@/chest/signup-sheet';
import { createMoney } from '@/ui/money-dom';
import type { PayoutFlight, QualityTier, StakeFlight } from '@/ui/money-flight';
import { openSignup, registerSignupHooks, viewerStore } from '@/chest/signup-state';
import { mountArrivalLayer } from '@/chest/arrival-layer';
import { arrivalAllowedOn, arrivalStore, chestsArriveAfterResults, endArrival, RESULTS_ARRIVAL_HOLD_MS, setArrivalGate } from '@/chest/arrival-state';
import { openNextChest, refreshChests } from '@/chest/chests-state';
import { devArrival } from '@/chest/dev';
import { liveQuality, recommendedQuality, type Quality } from '@/pixi/quality';
import { chestTier } from '@/chest/chest-tier';
import type { Account, AppNotification, ChestSummary, ChestView, LeaderboardBoard, LeaderboardPeriod } from '@/net/api';
import { CHESTS_ENABLED } from '@/core/flags';
import { openBestScores } from '@/ui/best-scores';
import type { TopBar } from '@/ui/screens';
import { createZoomGate } from '@/ui/zoom-guard';
import { createZoomHint } from '@/ui/zoom-hint';
import {
  ChallengeScreen,
  CreateChallengeScreen,
  ProfileScreen,
  HistoryScreen,
  TakeChallengeScreen,
  GuideScreen,
  hasSeenGuide,
  navDock,
  rememberGuide,
  type AudioMixer,
  type TopBarCallbacks,
} from '@/ui/screens';

export { Hud, formatClock, formatScore, prefersReducedMotion } from '@/ui/hud';
export { GuideScreen, type AudioMixer } from '@/ui/screens';
export { StartScreen } from '@/ui/home';
export { PauseScreen } from '@/ui/settings';

/* ---------------------------------------------------------------- tokens -- */

const VAR_PREFIX = '--w21-';

function kebab(key: string): string {
  return key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}

/** `#FFC53D` → `255 197 61`, for `rgb(var(--w21-gold-rgb) / 0.4)`. */
function rgbTriplet(hex: string): string {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return `${(value >> 16) & 0xff} ${(value >> 8) & 0xff} ${value & 0xff}`;
}

function shadow(step: { blur: number; opacity: number; y: number }): string {
  return `0 ${step.y}px ${step.blur}px rgb(var(${VAR_PREFIX}ink-rgb) / ${step.opacity})`;
}

/**
 * Publish the design tokens as CSS custom properties. `ui.css` contains no
 * colour literals — it reads everything from here, so the palette stays owned
 * by `@/core/tokens`.
 */
export function applyTokens(target: HTMLElement = document.documentElement): void {
  const style = target.style;
  for (const [key, value] of Object.entries(cssPalette)) {
    const name = kebab(key);
    style.setProperty(`${VAR_PREFIX}${name}`, value);
    style.setProperty(`${VAR_PREFIX}${name}-rgb`, rgbTriplet(value));
  }
  // The rank ramp, as numbered tokens. Numbered rather than named because the
  // consumer always has an index (`rank.index`) and never a name — and because
  // the names are shared across titles while the hexes are not.
  rankPalette.forEach((value, i) => {
    style.setProperty(`${VAR_PREFIX}rank-${i}`, value);
    style.setProperty(`${VAR_PREFIX}rank-${i}-rgb`, rgbTriplet(value));
  });
  // The shell's faces, not the game's — see `type` in @/core/tokens.
  style.setProperty(`${VAR_PREFIX}font-display`, typeTokens.shellDisplay);
  style.setProperty(`${VAR_PREFIX}font-body`, typeTokens.shellBody);
  style.setProperty(`${VAR_PREFIX}track`, typeTokens.displayTracking);
  style.setProperty(`${VAR_PREFIX}ui`, `${motion.ui}s`);
  style.setProperty(`${VAR_PREFIX}elev-1`, shadow(elevation.resting));
  style.setProperty(`${VAR_PREFIX}elev-2`, shadow(elevation.lifted));
}

function clearTokens(target: HTMLElement): void {
  const style = target.style;
  for (const key of Object.keys(cssPalette)) {
    const name = kebab(key);
    style.removeProperty(`${VAR_PREFIX}${name}`);
    style.removeProperty(`${VAR_PREFIX}${name}-rgb`);
  }
  rankPalette.forEach((_, i) => {
    style.removeProperty(`${VAR_PREFIX}rank-${i}`);
    style.removeProperty(`${VAR_PREFIX}rank-${i}-rgb`);
  });
  for (const name of ['font-display', 'font-body', 'track', 'ui', 'elev-1', 'elev-2']) {
    style.removeProperty(`${VAR_PREFIX}${name}`);
  }
}

/* ------------------------------------------------------------------ api -- */

/** Which of the three states the overlay is showing. */
export type UIScreen =
  | 'start'
  | 'game'
  | 'over'
  | 'history'
  | 'create'
  | 'take'
  | 'challenge'
  | 'profile'
  | 'leaderboard'
  | 'howitworks'
  | 'settings';

/**
 * Everything the overlay asks the app to do. All five are required — the UI
 * has no fallback behaviour and never mutates game state itself.
 */
export interface UIHandlers {
  /** Play was pressed on the start screen. Deal a round. */
  onStart(): void;
  /** A number key 1–4 was pressed. `pile` is 0-based. */
  onPlayPile(pile: number): void;
  /** Undo was pressed, or `U`. Only fires while the HUD reports undo available. */
  onUndo(): void;
  /** Pause was toggled — by button, `Space` or `Esc`. Stop or resume the clock. */
  onPause(paused: boolean): void;
  /** Play again was pressed on the game-over or pause screen. */
  onRestart(): void;
  /** Take the next deck from the shared pool and chase whoever set it. */
  onAsync(): void;
  /** Open a lobby and get a link to share. */
  onLobby(): void;
  /** Open a staked challenge at this fee. */
  onCreateChallenge(entryFee: number, isPrivate: boolean, useTicket: boolean | undefined): void;
  /** Take any open challenge at this fee. */
  onTakeChallenge(entryFee: number, useTicket?: boolean): void;
  /** The wager on the take screen moved: ask for the queue at that stake. */
  onTakeFee?(entryFee: number): void;
  /** Take one specific challenge by its share code. */
  onTakeCode(code: string): void;
  /** Play the round for the challenge currently on screen. */
  onPlayChallenge(): void;
  /** Open one challenge in full — from a history row. */
  onOpenChallenge(id: string): void;
  /**
   * Open a chest the player has earned.
   *
   * The UI does not talk to the network: it says which chest, and the host
   * opens it (the server applies the stored roll) and hands the roll back
   * through {@link UI.showChest}.
   */
  onOpenChest?(id: string): void;
  /** A chest notice the chest list has not heard (the stream's, or a foreground re-read's): re-read the list. */
  onChestNews?(): void;
  /** A challenge ended (a settle notice arrived): the host re-reads whichever of the player's own lists is open. */
  onChallengesChanged?(): void;
  /** The own profile's best-score tile: the host fetches the player's top rounds and calls `showBestScores`. */
  onBestScores?(): void;
  /** The profile's Rewards tab: data and equip (the host owns the network). */
  rewards?: import('@/ui/rewards').RewardsApi;
  /** A chest's $CHAIN has landed in the balance: refresh the account. */
  onChestBalance?(): void;
  /** Show a player's profile. Omit the name for the signed-in player's own. */
  onProfile(username?: string): void;
  /** A guest asking to keep their rank. */
  onCreateAccount(): void;
  /** End the session. */
  onSignOut(): void;
  /** Signed in to an existing account from the sheet: make it the session's. */
  onSignedIn?(user: Account): void;
  /** Whether the round on the table is being recorded against a challenge. */
  isRanked?(): boolean;
  /** Hand a ranked round in as it stands: the clock runs out on it. */
  onForfeit?(): void;
  /** The screen changed. Fired from every transition, before the new screen is shown. */
  onScreen?(screen: UIScreen): void;
  /** Show every challenge this player has a seat in. */
  onHistory(): void;
  /** Send the lobby link on, from the game-over screen. */
  onShare(): void;
  /** Share a SETTLED challenge from its own screen as "Challenge Me". */
  onShareChallenge?(view: ChallengeView): void;
  /** Deal the scripted walkthrough round on the real board. */
  onGuideStart(): void;
  /** Stop it, however it ended — skipped, finished, or interrupted. */
  onGuideEnd(): void;
  /**
   * Home was pressed on the game-over screen. Put the match back to solo: the
   * challenge that was in force has been played and paid off, and the start
   * screen the player is about to see offers "Play solo" — which must not
   * quietly deal somebody else's deck again.
   */
  onHome(): void;
}

export interface UIOptions {
  handlers: UIHandlers;
  /** Mount point. An element, a selector, or `#ui-root` by default. */
  root?: HTMLElement | string;
  /** Event bus to listen on. Defaults to the shared bus. */
  bus?: EventBus;
  /** Subscribe to the bus. Default true. */
  autoBind?: boolean;
  /**
   * Put the game-over screen up as soon as the engine says the round is over.
   * Default true.
   *
   * The engine ends the round the instant the last card resolves, which is
   * several hundred milliseconds before the table has finished showing what
   * that card was worth. A host that would rather let the flourish land can set
   * this false and call {@link UI.showGameOver} when it is ready.
   */
  autoGameOver?: boolean;
  /** Round length for the timer track. Defaults to 180. */
  roundSeconds?: number;
  /** Cards in a full stock, for the opening readout. Defaults to 52. */
  totalCards?: number;
  /** Start on the start screen. Default true; pass false to open straight into play. */
  showStart?: boolean;
  /**
   * Mixer for the pause screen's sound controls. Omit and the pause screen has
   * no sound section — the UI does not import the audio engine, it is handed
   * something that can turn things up and down.
   */
  audio?: AudioMixer;
  /** The quality tier, for the settings sheet: read, and pinned. */
  quality?: { get(): QualityChoice; set(choice: QualityChoice): void };
}

export interface UI {
  /** Say how the round went against whoever else played the deck. */
  setVerdict: GameOverScreen['setVerdict'];
  /**
   * A stake is about to leave the balance: hold the pill, then `finish` with the pot card's coin once
   * the account has been told the new balance. Call before `setAccount`.
   */
  beginStake(): StakeFlight;
  /** The pot card's coin on the screen the player is on (create, take), for the stake flight to land on. */
  potCoin(): Element | null;
  /** The quality tier, so a low one plays a shorter coin stream. */
  setQualityTier(tier: QualityTier): void;
  /** A line on the challenge page above "Play your game" (why the last try did not start), or null to clear. */
  setChallengeNotice(text: string | null): void;
  /**
   * The chests this round earned, unopened.
   *
   * The results tray then offers them: a rank-up's CONTINUE opens the one it
   * earned, and anything left over opens from the inbox.
   */
  setEarnedChests(chests: readonly ChestSummary[]): void;
  /** Show a chest's stored roll, and animate it. */
  showChest(roll: ChestView): void;
  /** The results tray's Share button, while the cards render and upload. */
  setShareBusy(busy: boolean): void;
  /** The settled challenge screen's Share button, while its cards render and upload. */
  setChallengeShareBusy(busy: boolean): void;
  /** Scroll the profile on screen to its "Play <name>" card (a Challenge Me link's `#challenge`). */
  focusProfileChallenge(): boolean;
  /** Tell the player what the network is doing, on the start screen. */
  setStartStatus: StartScreen['setStatus'];
  setStartBusy: StartScreen['setBusy'];
  /** Collapse the start screen to a single "Accept challenge" on a shared link. */
  setStartInvite: StartScreen['setInvite'];
  /** The score to beat, above the table. Hidden until there is one. */
  setTarget: StartScreen['setTarget'];
  /** Who is signed in and what they hold. Null hides the strip. */
  setAccount: StartScreen['setAccount'];
  /** The daily top-up landed — say so, once, under the balance. */
  showDailyGrant: StartScreen['showDailyGrant'];
  /** The player's last few challenges, for the home card and their profile. */
  setRecent(rows: ChallengeCard[]): void;
  /** The XP this round earned, under the breakdown. */
  showXp: GameOverScreen['showXp'];
  /** Every challenge this player has a seat in. */
  showHistory(rows: ChallengeCard[], record?: ChallengeRecord | null): void;
  /** History again from a newer fetch, in place (scroll kept, no blank in between). Nothing if History is not up. */
  refreshHistory(rows: ChallengeCard[], record?: ChallengeRecord | null): void;
  /** The viewer's own profile's Recent challenges again from a newer fetch, in place. Nothing if that page is not up. */
  refreshProfileRecent(rows: ChallengeCard[]): void;
  /** The home Recent card again from a newer fetch, in place. Nothing is drawn unless home is up. */
  refreshHomeRecent(rows: ChallengeCard[]): void;
  /** The own profile's money card and stat tiles again from a newer read, in place. Nothing if that page is not up. */
  refreshProfileStats(profile: Profile, finance: Finance | null): void;
  /** True while the profile up is the viewer's own. */
  profileIsOwn(): boolean;
  /** The leaderboard, on a board and period (Blockari's `/leaderboard`). */
  showLeaderboard(board?: LeaderboardBoard, period?: LeaderboardPeriod): void;
  /** The How-it-works reference: rules, scoring, XP, chests, the 2x bonus, the profile ring. */
  showHowItWorks(section?: string): void;
  showSettings(): void;
  /** Pick a stake and open a challenge. */
  /** `balance` is null while the account is not known (nothing is refused for affordability until it is). */
  showCreate(bounds: FeeBounds, balance: number | null): void;
  /** The viewer's own open challenges, counted on the create screen. */
  /** The quiet "You have created / taken N challenges" lines on Create and Take. */
  setChallengeCounts(counts: import('@/net/api').ChallengeCounts | null): void;
  /** Free-entry tickets held, by wager: the create and take screens offer them. */
  setTickets(tickets: Record<string, number>): void;
  /** The best-scores sheet over the profile. */
  showBestScores(rows: readonly import('@/net/api').BestScore[] | null): void;
  /** Find a match, or enter a code. */
  showTake(bounds: FeeBounds, queues: Record<string, QueueTier>, balance: number | null): void;
  /** The open queue's rows, beside the picker. */
  setQueue(rows: OpenChallenge[]): void;
  /** A poll's counts alone: the ladder and the line update, nothing else moves. */
  setQueues(queues: Record<string, QueueTier>): void;
  /** The fee chosen on the take screen, for the poll that asks for it first. */
  takeFee(): number;
  /** Whether the Take screen's CUSTOM tile is filtering the list (non-quick amounts), so the poll asks for those. */
  takeCustom(): boolean;
  /** Whether a take is in flight on the take screen — a poll waits for it. */
  takeBusy(): boolean;
  /** Fill the code field from a share link and open the take screen on it. */
  prefillTake(code: string): void;
  /** One challenge: the waiting room, and the scoreboard afterwards. */
  showChallenge(view: ChallengeView): void;
  /** Toasts, the unread badge and the inbox. Started and stopped with the session. */
  readonly notify: NotifyHost;
  /** A player's profile. */
  showProfile(profile: Profile, mine: boolean, finance?: Finance | null): void;
  /** Open the profile screen before the fetch lands. */
  openProfile(): void;
  failProfile(message: string): void;
  /** A create or take that failed, reported on whichever screen asked. */
  failCreate(message: string): void;
  failTake(message: string): void;
  /** The line narrating the walkthrough round. */
  setGuideCaption(text: string): void;
  /** The walkthrough has played out; move on to the round that was asked for. */
  endGuide(): void;
  /** How much room the walkthrough's narration is taking, in CSS pixels. */
  guideInset(): number;
  /** Drop the walkthrough's heading now the demo is doing the explaining. */
  compactGuide(): void;
  readonly root: HTMLElement;
  readonly hud: Hud;
  /** The state currently on screen. */
  readonly screen: UIScreen;
  readonly paused: boolean;
  /** True while the game-over breakdown is still counting up. */
  readonly revealing: boolean;

  showStart(): void;
  showGame(): void;
  showGameOver(final: FinalScore): void;
  /** Push a snapshot into every readout. Idempotent. */
  sync(snapshot: GameSnapshot): void;
  setPaused(paused: boolean): void;
  togglePause(): boolean;
  /** Speak a line to screen readers. */
  announce(message: string): void;
  destroy(): void;
}

/* -------------------------------------------------------------- mounting -- */

function resolveRoot(root: UIOptions['root']): HTMLElement {
  if (root instanceof HTMLElement) return root;
  if (typeof root === 'string') {
    const found = document.querySelector(root);
    if (found instanceof HTMLElement) return found;
  }
  const existing = document.getElementById('ui-root');
  if (existing) return existing;
  const created = el('div');
  created.id = 'ui-root';
  document.body.append(created);
  return created;
}

/** Digits 1–4 from either the character or the physical key. */
function pileFromKey(event: KeyboardEvent): number | null {
  if (event.key.length === 1 && event.key >= '1' && event.key <= '4') {
    return Number(event.key) - 1;
  }
  const match = /^(?:Digit|Numpad)([1-4])$/.exec(event.code);
  return match ? Number(match[1]) - 1 : null;
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return /^(?:input|textarea|select)$/i.test(target.tagName);
}

function isButton(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.closest('button') !== null;
}

/** Short screen-reader line for a resolution. Plain landings stay quiet. */
function describe(resolution: Resolution): string | null {
  const bits: string[] = [];
  if (resolution.is21) bits.push('twenty-one');
  if (resolution.isFiveCard) bits.push('five cards');
  if (resolution.isWild) bits.push('wild');
  if (resolution.isBust) bits.push('bust');
  if (resolution.streakLabel !== null) bits.push(`${resolution.streakLabel} streak`);
  if (bits.length === 0) return null;
  const points =
    resolution.pointsGained > 0 ? `plus ${formatScore(resolution.pointsGained)}` : 'no score';
  return `${bits.join(', ')}, ${points}. Total ${formatScore(resolution.score)}.`;
}

/**
 * Build the overlay, wire it to the bus and return the handle.
 * Call `destroy()` to remove every listener and node it created.
 */
export function mountUI(options: UIOptions): UI {
  const handlers = options.handlers;
  const eventBus = options.bus ?? sharedBus;
  const root = resolveRoot(options.root);
  const tokenTarget = document.documentElement;

  applyTokens(tokenTarget);
  root.classList.add('w21');
  root.dataset.screen = 'start';
  root.dataset.paused = 'false';

  let screen: UIScreen = 'start';
  let paused = false;
  let snapshot: GameSnapshot | null = null;
  let destroyed = false;

  // No pinch-zoom while a round is on the table (iOS ignores user-scalable=no); if the page gets
  // zoomed anyway the guard snaps it back, and only when that fails does the toast show. Every
  // other screen never arms it, so they keep the browser's own zoom.
  const zoomHint = createZoomHint(root);
  const zoomGate = createZoomGate({ onStuck: (stuck) => zoomHint.setStuck(stuck) });

  const hud = new Hud({
    roundSeconds: options.roundSeconds ?? ROUND_SECONDS,
    ...(options.totalCards === undefined ? {} : { totalCards: options.totalCards }),
    callbacks: {
      onUndo: () => {
        if (screen === 'game' && !paused) handlers.onUndo();
      },
      onPause: () => {
        togglePause();
      },
    },
  });

  /**
   * The walkthrough, once, before a first-timer's first round.
   *
   * Wrapped around every way in rather than just "play solo": somebody who
   * arrives on a friend's challenge link is the *most* likely person here never
   * to have played, and they press a different button.
   *
   * The demo itself is a real round on the real board — see `runGuide` in
   * `@/main` — so this only holds what the player is meant to do next.
   */
  let afterGuide: (() => void) | null = null;

  const finishGuide = (): void => {
    const next = afterGuide;
    afterGuide = null;
    rememberGuide();
    guide.hide();
    handlers.onGuideEnd();
    next?.();
  };

  const guide = new GuideScreen({ onSkip: () => finishGuide() });

  /**
   * Run the walkthrough, then do `run`.
   *
   * `label` promises what that is: on a first run a round is already waiting to
   * be dealt, and on a tutorial somebody asked to watch, they go back to the
   * screen they pressed it on.
   */
  const walkthrough = (run: () => void, label?: string): void => {
    afterGuide = run;
    showGame();
    guide.show(label);
    handlers.onGuideStart();
  };

  const explainFirst = (run: () => void): void => {
    if (hasSeenGuide()) {
      run();
      return;
    }
    walkthrough(run);
  };

  /**
   * The shell bar, wired once. Every screen gets this same object, so the
   * brand, the nav, the pill and the disc do the same thing on every route —
   * which is what Blockari's shell does, and the property whose absence left
   * the History screen's bar inert. Targets are Blockari's: the pill opens the
   * profile (`.balance` → `/profile`), the disc opens the inbox (`.avatar-link`
   * → the notifications popover).
   */
  const bar: TopBarCallbacks = {
    onHome: () => showStart(),
    onBalance: () => handlers.onProfile(),
    onAvatar: () => openAvatar(),
    onPlay: () => explainFirst(() => beginRound(false)),
    // Create and Take open a screen, they do not deal a round, so they are not behind the walkthrough (the home
    // screen's two cards are not either): the dock under a profile sent a player who had not seen it into the
    // tutorial instead of the screen they pressed.
    onCreate: () => handlers.onLobby(),
    onTake: () => handlers.onAsync(),
    onHistory: () => handlers.onHistory(),
    onLeaderboard: () => showLeaderboard(),
    onGuide: () => showHowItWorks('bonus'),
  };

  const startScreen = new StartScreen({
    bar,
    onLeaderboard: () => showLeaderboard(),
    onStart: () => explainFirst(() => beginRound(false)),
    // Not wrapped in `explainFirst`: these open a screen, they do not deal a
    // round. The walkthrough belongs immediately before the first hand, not
    // before a stake has even been chosen — a tutorial that plays and then
    // drops the player on a fee ladder has taught them the wrong thing about
    // what the button did.
    onAsync: () => handlers.onAsync(),
    onLobby: () => handlers.onLobby(),
    // Asked for outright, so it always plays and always comes back here.
    onTutorial: () => walkthrough(() => showStart(), 'Done'),
    onGuide: () => showHowItWorks(),
    onSignOut: () => handlers.onSignOut(),
    onCreateAccount: () => openSignup('prompt'),
    onHistory: () => handlers.onHistory(),
    onProfile: () => handlers.onProfile(),
    onOpenChallenge: (id) => handlers.onOpenChallenge(id),
  });
  const leaderboardScreen = new LeaderboardScreen({
    bar,
    onProfile: (username) => handlers.onProfile(username),
    onSignUp: () => openSignup('board'),
  });
  const howScreen = new HowItWorksScreen({
    bar,
    onHome: () => showStart(),
    onHistory: () => handlers.onHistory(),
    onProfile: () => handlers.onProfile(),
  });
  const settingsScreenUI = new SettingsScreen(
    {
      bar,
      // Back to where the player came from; a page reached with nowhere to go back to goes home.
      onClose: () => (settingsFrom && settingsFrom !== 'settings' ? returnTo(settingsFrom) : showStart()),
      onHome: () => showStart(),
      onHistory: () => handlers.onHistory(),
    },
    { audio: options.audio, quality: options.quality },
  );
  const historyScreen = new HistoryScreen({
    bar,
    onBack: () => showStart(),
    onOpen: (id) => handlers.onOpenChallenge(id),
  });
  const createScreenUI = new CreateChallengeScreen({
    bar,
    onCreate: (fee, isPrivate, useTicket) => handlers.onCreateChallenge(fee, isPrivate, useTicket),
    onBack: () => showStart(),
    onHistory: () => handlers.onHistory(),
    onHome: () => showStart(),
    onProfile: () => handlers.onProfile(),
    onOpenChallenge: (id) => handlers.onOpenChallenge(id),
  });
  const takeScreenUI = new TakeChallengeScreen({
    bar,
    // These two DO deal, so the walkthrough gates them.
    onMatch: (fee, useTicket) => explainFirst(() => handlers.onTakeChallenge(fee, useTicket)),
    onCode: (code) => explainFirst(() => handlers.onTakeCode(code)),
    onFee: (fee) => handlers.onTakeFee?.(fee),
    onCreate: () => handlers.onLobby(),
    onBack: () => showStart(),
    onHistory: () => handlers.onHistory(),
    onHome: () => showStart(),
    onProfile: () => handlers.onProfile(),
  });
  const profileScreenUI = new ProfileScreen({
    bar,
    onBestScores: () => handlers.onBestScores?.(),
    onGuide: () => showHowItWorks(),
    onSettings: () => showSettings(),
    ...(CHESTS_ENABLED && handlers.rewards ? { rewards: handlers.rewards } : {}),
    onBack: () => showStart(),
    onHistory: () => handlers.onHistory(),
    onHome: () => showStart(),
    onProfile: () => handlers.onProfile(),
    onOpenProfile: (username) => handlers.onProfile(username),
    games: userGames,
    streaks: userStreaks,
    onCreateAccount: () => openSignup('prompt'),
    onChallenge: () => handlers.onLobby(),
    onTake: () => handlers.onAsync(),
  });
  const challengeScreenUI = new ChallengeScreen({
    bar,
    onPlay: () => explainFirst(() => handlers.onPlayChallenge()),
    onHistory: () => handlers.onHistory(),
    onHome: () => showStart(),
    onProfile: (username) => handlers.onProfile(username),
    onShare: (view) => handlers.onShareChallenge?.(view),
  });
  const pauseScreen = new PauseScreen({
    // Opened as SETTINGS from the results screen's gear, the sheet is up with
    // nothing paused; Done just puts it away.
    onResume: () => (paused ? setPaused(false) : pauseScreen.hide()),
    onRestart: () => {
      setPaused(false);
      beginRound(true);
    },
    onQuit: () => {
      setPaused(false);
      handlers.onHome();
      showStart();
    },
    // A ranked round handed in as it stands: the clock runs out on it.
    onForfeit: () => {
      setPaused(false);
      // Off at once: the round's end plays out on the board for the next few
      // seconds (the sweep, the hit-stop, the plate), and a sheet still
      // offering Forfeit over it reads as a press that did nothing.
      pauseScreen.hide();
      handlers.onForfeit?.();
    },
  }, { audio: options.audio, quality: options.quality });
  const overScreen = new GameOverScreen({
    onRestart: () => beginRound(true),
    onShare: () => handlers.onShare(),
    onSeeChallenge: (id) => handlers.onOpenChallenge(id),
    onTakeAnother: () => handlers.onAsync(),
    onCreateAnother: () => handlers.onLobby(),
    onSettings: () => pauseScreen.show(),
    onHome: () => {
      handlers.onHome();
      // The invitation is spent, so the start screen goes back to being a menu
      // rather than still offering to accept a challenge already played.
      startScreen.setInvite(false);
      startScreen.setStatus('');
      showStart();
    },
  });

  /**
   * The chests: Blockari's, cloned (src/chest). A chest opened from the inbox,
   * the profile or the top bar's pill plays its reveal on a stage of its own
   * over everything; the chests a round earns ARRIVE — over the results after
   * the rank-up's CONTINUE or once the XP beat settles, over home when earned
   * away — through the arrival layer.
   */
  let earned: readonly ChestSummary[] = [];
  const chestQuality = (): Quality => {
    const q = options.quality?.get();
    return chestTier(q === 'low' || q === 'medium' || q === 'high' ? q : 'auto', liveQuality(), {
      dpr: window.devicePixelRatio || 1,
      recommended: recommendedQuality(),
      coarse: window.matchMedia?.('(pointer: coarse)').matches === true,
    });
  };
  // Dev builds only (folded out of production): what a chest opened right now would draw at, for the governor probes.
  if (import.meta.env.DEV) (globalThis as unknown as Record<string, unknown>).__w21ChestTier = chestQuality;
  let reveal: ChestStage | null = null;
  const onChestBalance = (): void => handlers.onChestBalance?.();
  if (CHESTS_ENABLED) mountArrivalLayer({ quality: chestQuality, onBalance: onChestBalance });
  setArrivalGate(() => arrivalAllowedOn(screen));
  // Guest → account: the sign-up sheet, its celebration and the once-a-day nudge. The account is the
  // same one after registering, so the session just re-reads it (and the chests released arrive).
  const signup = mountSignupLayer();

  // Money in motion: what the balance pill SHOWS is decided by the display, which holds it for a coin
  // flight and hands back the server's number at the end (money-flight.ts). Every screen carries its own
  // bar (the results screen its own pill), so the display paints all of them.
  let flightTier: QualityTier = 'high';
  const money = createMoney({
    paintBars: (n) => {
      for (const bar of everyTopBar()) bar.setBalance(n);
      overScreen.setBalance(n);
    },
    tier: () => flightTier,
    results: { show: () => overScreen.showPill(), coin: () => overScreen.pillCoin() },
  });
  /** Two frames on, so the layout the flight measures is the one the player is looking at. */
  const afterPaint = (fn: () => void): void => {
    if (typeof requestAnimationFrame !== 'function') return fn();
    requestAnimationFrame(() => requestAnimationFrame(fn));
  };
  registerSignupHooks({
    accountChanged: () => onChestBalance(),
    signedIn: (user) => handlers.onSignedIn?.(user),
    closeReveal: () => {
      reveal?.destroy();
      reveal = null;
    },
  });

  const screensLayer = el('div', 'w21-screens');
  screensLayer.append(
    startScreen.root,
    guide.root,
    pauseScreen.root,
    overScreen.root,
    historyScreen.root,
    leaderboardScreen.root,
    howScreen.root,
    settingsScreenUI.root,
    createScreenUI.root,
    takeScreenUI.root,
    challengeScreenUI.root,
    profileScreenUI.root,
  );

  // An inactive screen is out of the document, not merely invisible. The
  // `data-active` CSS hides one with `visibility`, which keeps its boxes:
  // during a round the Create and Take screens were laid out under the
  // canvas with real, focusable buttons in the tab order, and the results
  // tray sat 553 px wide under every shell screen. `hidden` is global and
  // wins over every `display` (b7b7fc6), so it follows `data-active` here,
  // for every screen, rather than in each screen's own show/hide.
  //
  // A route change lands at the top, as Blockari's does, and THIS is where that is done: each screen
  // is its own scroller, and a `display: none` element keeps its scroll offset and restores it when
  // it is shown again (a `scrollTop = 0` written to an element that is already hidden is a no-op),
  // so the offset is cleared on the way out, while the box still exists. Every screen, not only the
  // ones with a page to scroll; and `showHowItWorks(section)`, which scrolls after the screen is up,
  // is untouched.
  const followActive = (screen: Element): void => {
    if (!(screen instanceof HTMLElement)) return;
    const active = screen.dataset.active === 'true';
    if (!active && !screen.hidden) screen.scrollTop = 0;
    screen.hidden = !active;
  };
  for (const screen of screensLayer.children) followActive(screen);
  const activeWatch = new MutationObserver((records) => {
    for (const record of records) followActive(record.target as Element);
  });
  for (const screen of screensLayer.children) {
    activeWatch.observe(screen, { attributes: true, attributeFilter: ['data-active'] });
  }

  // `.scrolled` on EVERY route, not just the start screen — one place, as
  // Blockari has one listener in the shell. Each screen is its own scroller,
  // so each is listened to; the bar it owns is the one that changes. The
  // start screen wires its own as well (it also drives the brand swap), and
  // toggling a class twice is not a problem.
  // The nav's lit link goes with it: the bars are all told which route is up.
  const routed: Array<[{ root: HTMLElement; topBar: TopBar }, string]> = [
    [historyScreen, 'history'],
    [leaderboardScreen, 'leaderboard'],
    [howScreen, ''],
    [settingsScreenUI, ''],
    [createScreenUI, 'create'],
    [takeScreenUI, 'take'],
    [challengeScreenUI, ''],
    [profileScreenUI, ''],
    [startScreen, ''],
  ];
  for (const [ui, route] of routed) {
    ui.root.addEventListener('scroll', () => ui.topBar.setScrolled(ui.root.scrollTop > 40), {
      passive: true,
    });
    ui.topBar.setActive(route);
  }

  // The round's chrome — ✕ and ⚙, as on the results screen (which carries
  // its own) — docked in the HUD's bottom row, bottom right, where Adam put
  // it: at the top they covered the bust dots and the clock. The HUD shows
  // and hides with the round, so the chrome needs no toggling of its own.
  // ✕ opens the sheet too: Quit and Forfeit live there, and a stray tap must
  // not end a round outright.
  hud.dock(
    gameChrome(
      () => openSheet(),
      () => openSheet(),
    ),
  );

  const liveRegion = el('div', 'w21-sr-live');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');

  const notify = new NotifyHost({
    onOpenChallenge: (id: string) => handlers.onOpenChallenge(id),
    onOpenChest: (id: string) => handlers.onOpenChest?.(id),
    onChestNews: () => handlers.onChestNews?.(),
    onChallengesChanged: () => handlers.onChallengesChanged?.(),
    // The PROFILE, not the inbox. A bulk rewrite that pointed every top bar's
    // avatar at the inbox caught this one too, so the inbox's own "Profile →"
    // reopened the inbox — from the player's side, a link that did nothing.
    onProfile: () => handlers.onProfile(),
    onLeaderboard: () => showLeaderboard(),
    onGuide: () => showHowItWorks(),
    onSettings: () => showSettings(),
    onBalanceDelta: (amount: number, n) => applyBalanceDelta(amount, n),
    onToastShown: (n, toast) => payoutToastShown(n, toast),
    onUnread: (count: number) => {
      for (const bar of everyTopBar()) bar.setUnread(count);
    },
  });

  root.append(hud.root, screensLayer, liveRegion, notify.toasts.root, notify.inbox.root);

  /**
   * The phone's Create / Take dock (Blockari's QuickNav): on every signed-in shell screen but home, the
   * sign-in and the game itself. Its room is reserved by the page's bottom padding (`.w21-has-dock`).
   */
  const dock = navDock(bar, () => {
    // The lit button is the screen already up: back to its top, smoothly unless motion is reduced.
    const active = screensLayer.querySelector<HTMLElement>('.w21-screen[data-active="true"]');
    const calm = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    active?.scrollTo({ top: 0, behavior: calm ? 'auto' : 'smooth' });
  });
  root.append(dock.root);
  const DOCKED: readonly UIScreen[] = ['profile', 'history', 'leaderboard', 'howitworks', 'settings', 'create', 'take', 'challenge'];
  function updateDock(): void {
    const visible = shownAccount !== null && DOCKED.includes(screen);
    dock.show(screen === 'create' || screen === 'take' ? screen : null, visible);
    for (const page of root.querySelectorAll('.w21-page')) page.classList.toggle('w21-has-dock', visible);
  }

  /* ------------------------------------------------------------- screens -- */

  /** The challenge the current screen is about, if it is about one. */
  let currentChallengeId: string | null = null;

  /**
   * The account the bars are currently showing, so a notification's delta has
   * something to be added to.
   */
  let shownAccount: Parameters<StartScreen['setAccount']>[0] = null;

  /**
   * Move the balance the moment the player is told, not a round trip later.
   *
   * The notification already carries the delta, so waiting for `/auth/me`
   * would only mean the pill sits on a stale number while a toast beside it
   * announces the new one. The next refresh reconciles — and lands on the same
   * value, which `setAccount` will not re-tween because the number is equal.
   */
  function applyBalanceDelta(amount: number, n?: AppNotification): void {
    if (!shownAccount) return;
    // A payout (a win settled elsewhere, a refund) with a toast to announce it: the pill holds its number,
    // and the toast's coin brings the money in (`payoutToastShown`). Anything else — no pill on this
    // screen, motion reduced, this payout already flown — moves the number now, as it always did.
    if (n && amount > 0 && n.challengeId) {
      const id = `${n.kind === 'challenge_expired' || n.kind === 'challenge_voided' ? 'refund' : 'win'}:${n.challengeId}`;
      const flight = money.flights.beginPayout({ id, payout: amount });
      if (flight) pendingPayouts.set(n.id, flight);
    }
    ui.setAccount({ ...shownAccount, balance: shownAccount.balance + amount });
  }

  /** Payouts waiting for their toast, by notification id. */
  const pendingPayouts = new Map<string, PayoutFlight>();

  /** The toast is up: its amount's coin (or, with no amount, the toast itself) flies into the pill. */
  function payoutToastShown(n: AppNotification, toast: HTMLElement): void {
    const flight = pendingPayouts.get(n.id);
    if (!flight) return;
    pendingPayouts.delete(n.id);
    afterPaint(() => {
      const figure = toast.querySelector('.w21-nt-amount .w21-coin') ?? toast.querySelector('.w21-nt-body');
      void flight.finish(figure);
    });
  }

  /**
   * The avatar opens the inbox, not the profile.
   *
   * It is the only persistent target in the shell and it now carries a count,
   * so tapping it has to answer the question the count asks. The profile is
   * one tap further, from the panel's own footer — which is also where a
   * player who came looking for it will now expect it.
   */
  function openAvatar(): void {
    notify.openInbox();
  }

  /** Every bar on screen and off it — the same list `setAccount` writes to. */
  function everyTopBar(): readonly TopBar[] {
    return [
      startScreen.topBar,
      createScreenUI.topBar,
      profileScreenUI.topBar,
      takeScreenUI.topBar,
      challengeScreenUI.topBar,
      historyScreen.topBar,
      leaderboardScreen.topBar,
      howScreen.topBar,
      settingsScreenUI.topBar,
    ];
  }

  function setScreen(next: UIScreen): void {
    screen = next;
    zoomGate.screen(next);
    // Game start (and a replay, which is `game` again): a zoom that crept in before it is undone first.
    if (next === 'game') zoomGate.check();
    // A round starting takes the table: an arrival already up (it began while a start was in flight)
    // gives way, and its chests stay in the pill.
    if (next === 'game' && arrivalStore.getState().batch) endArrival();
    signup.notifyScreen(next);
    root.dataset.screen = next;
    updateDock();
    handlers.onScreen?.(next);
    // A round is the whole round — the count-in and a pause included. A toast
    // over a paused board is a toast over a board about to be resumed.
    notify.setContext({
      // The results screen counts as still in the round HERE: the backlog is
      // released by `setVerdict`, once the tray knows which challenge it is
      // about, so that challenge's own settlement is read silently rather
      // than flushed and then dropped. Everything else lands on the tray, as
      // Blockari flushes onto its results.
      inRound: next === 'game' || next === 'over',
      // The two screens that are ABOUT one challenge. Anything arriving for it
      // is marked read silently: the screen already says it.
      challengeId: next === 'over' || next === 'challenge' ? currentChallengeId : null,
    });
    hud.setVisible(next === 'game');
    hud.setEnabled(next === 'game' && !paused);

    if (next !== 'start') startScreen.hide();
    if (next !== 'over') overScreen.hide();
    if (next !== 'history') historyScreen.root.dataset.active = 'false';
    if (next !== 'leaderboard') leaderboardScreen.root.dataset.active = 'false';
    if (next !== 'howitworks') howScreen.root.dataset.active = 'false';
    if (next !== 'settings') settingsScreenUI.root.dataset.active = 'false';
    if (next !== 'create') createScreenUI.root.dataset.active = 'false';
    if (next !== 'take') takeScreenUI.root.dataset.active = 'false';
    if (next !== 'challenge') challengeScreenUI.root.dataset.active = 'false';
    if (next !== 'profile') profileScreenUI.root.dataset.active = 'false';
    if (next !== 'game' && paused) {
      // Leaving the table always releases the pause, and the app is told so its
      // clock does not stay stopped behind a screen it can no longer see.
      paused = false;
      root.dataset.paused = 'false';
      pauseScreen.hide();
      handlers.onPause(false);
    } else if (next !== 'game' && pauseScreen.isOpen) {
      // A ranked round's sheet pauses nothing, so nothing above releases it —
      // and a Forfeit pressed on it ends the round underneath. The sheet
      // closes the moment the game ends, as Blockari's does; the plate is
      // what the player pressed for.
      pauseScreen.hide();
    }
  }

  function showStart(): void {
    hud.reset();
    snapshot = null;
    setScreen('start');
    startScreen.show();
  }

  /** Blockari's `/leaderboard`: XP · this week unless a board or period is asked for. */
  function showLeaderboard(board?: LeaderboardBoard, period?: LeaderboardPeriod): void {
    setScreen('leaderboard');
    leaderboardScreen.root.dataset.active = 'true';
    leaderboardScreen.show(board, period);
  }

  /** Where Settings was opened from, so its close button goes back there. */
  let settingsFrom: UIScreen | null = null;
  function returnTo(from: UIScreen): void {
    if (from === 'profile') handlers.onProfile();
    else if (from === 'history') handlers.onHistory();
    else if (from === 'leaderboard') showLeaderboard();
    else if (from === 'howitworks') showHowItWorks();
    else if (from === 'create') handlers.onLobby();
    else if (from === 'take') handlers.onAsync();
    else showStart();
  }
  function showSettings(): void {
    settingsFrom = screen === 'settings' ? settingsFrom : screen;
    setScreen('settings');
    settingsScreenUI.root.dataset.active = 'true';
    settingsScreenUI.show();
  }

  function showHowItWorks(section?: string): void {
    setScreen('howitworks');
    howScreen.root.dataset.active = 'true';
    howScreen.show();
    if (section) howScreen.root.querySelector(`#w21-gd-${section}`)?.scrollIntoView();
  }

  function showHistory(rows: ChallengeCard[], record?: ChallengeRecord | null): void {
    setScreen('history');
    historyScreen.show(rows, record);
    historyScreen.root.dataset.active = 'true';
  }

  function refreshHistory(rows: ChallengeCard[], record?: ChallengeRecord | null): void {
    if (screen === 'history') historyScreen.refresh(rows, record);
  }

  function refreshHomeRecent(rows: ChallengeCard[]): void {
    profileScreenUI.setRecent(rows);
    if (screen === 'start') startScreen.refreshRecent(rows);
  }

  function refreshProfileStats(profile: Profile, finance: Finance | null): void {
    if (screen === 'profile' && profileScreenUI.showingOwn) profileScreenUI.refreshStats(profile, finance);
  }

  function refreshProfileRecent(rows: ChallengeCard[]): void {
    if (screen === 'profile' && profileScreenUI.showingOwn) profileScreenUI.refreshRecent(rows);
  }

  function showCreate(bounds: FeeBounds, balance: number | null): void {
    setScreen('create');
    createScreenUI.show(bounds, balance);
    createScreenUI.root.dataset.active = 'true';
  }

  function showTake(bounds: FeeBounds, queues: Record<string, QueueTier>, balance: number | null): void {
    setScreen('take');
    takeScreenUI.show(bounds, queues, balance);
    takeScreenUI.root.dataset.active = 'true';
  }

  function showChallenge(view: ChallengeView): void {
    // Recorded before `setScreen` runs, so the context it reports is this
    // challenge rather than whichever one was open last.
    currentChallengeId = view.id;
    setScreen('challenge');
    challengeScreenUI.show(view);
    challengeScreenUI.root.dataset.active = 'true';
    // A settled win the player has not seen paid: its coins leave the `+N $CHAIN` line and land in the
    // pill on this screen. Once per challenge id — the results flight for the same win counts. Started
    // NOW rather than after a paint: the pill has to read the balance without the payout as the page
    // comes in (it already includes it), and the coins follow once the page has settled.
    const payout = view.result?.won === true ? view.result.payout : 0;
    const figure = payout > 0 ? challengeScreenUI.root.querySelector('.w21-payfig') : null;
    if (figure) {
      void money.flights.win({ id: `win:${view.id}`, payout, pot: payout, figure, included: true, delayMs: 260 });
    }
  }

  function showProfile(profile: Profile, mine: boolean, finance?: Finance | null): void {
    setScreen('profile');
    profileScreenUI.show(profile, mine, finance);
    profileScreenUI.root.dataset.active = 'true';
  }

  function openProfile(): void {
    setScreen('profile');
    profileScreenUI.pending();
    profileScreenUI.root.dataset.active = 'true';
  }

  function showGame(): void {
    setScreen('game');
  }

  function showGameOver(final: FinalScore): void {
    // What the player is looking at as the round ends: a win's coins count up from THIS, whichever
    // order the balance refresh and the flight turn up in.
    money.display.mark();
    setScreen('over');
    overScreen.show(final);
    announce(`Round over. Final score ${formatScore(final.total)}.`);
  }

  /** Play / Play again — flip to the table first so the deal has somewhere to land. */
  function beginRound(restart: boolean): void {
    hud.reset();
    showGame();
    if (restart) handlers.onRestart();
    else handlers.onStart();
  }

  /* -------------------------------------------------------------- pause -- */

  /**
   * The sheet during a round. A solo round PAUSES under it; a ranked one
   * never does — the clock is the server's and keeps running — so the sheet
   * opens as Settings with "The clock keeps running" and Back to the game,
   * and Forfeit under it. Blockari's rule, and the only honest one: a
   * challenge paused on one side is a challenge with two clocks.
   */
  function openSheet(): void {
    if (screen !== 'game') return;
    const ranked = handlers.isRanked?.() ?? false;
    if (ranked) {
      pauseScreen.show({
        score: snapshot?.score ?? 0,
        secondsLeft: snapshot?.secondsRemaining ?? ROUND_SECONDS,
        mode: 'Challenge',
        ranked: true,
        running: true,
      });
      return;
    }
    setPaused(true);
  }

  function setPaused(next: boolean): void {
    if (next === paused) return;
    if (next && screen !== 'game') return;
    paused = next;
    root.dataset.paused = next ? 'true' : 'false';
    hud.setEnabled(!next);
    if (next) {
      pauseScreen.show({
        score: snapshot?.score ?? 0,
        secondsLeft: snapshot?.secondsRemaining ?? ROUND_SECONDS,
        mode: 'Solo',
        ranked: false,
        running: false,
      });
    } else {
      pauseScreen.hide();
      // Resume: a zoom that crept in behind the dialog is undone before the clock runs.
      zoomGate.check();
    }
    handlers.onPause(next);
  }

  function togglePause(): boolean {
    if (screen !== 'game') return paused;
    if (paused) setPaused(false);
    else if (pauseScreen.isOpen) pauseScreen.hide();
    else openSheet();
    return paused;
  }

  /* --------------------------------------------------------------- data -- */

  function sync(next: GameSnapshot): void {
    snapshot = next;
    hud.sync(next);
  }

  function announce(message: string): void {
    liveRegion.textContent = message;
  }

  function canPlay(): boolean {
    if (screen !== 'game' || paused || snapshot === null) return false;
    if (snapshot.activeCard === null) return false;
    // `resolving` is the phase carried by the snapshots emitted mid-play; the
    // engine is back in `playing` by the time a key can actually arrive.
    return snapshot.phase === 'playing' || snapshot.phase === 'resolving';
  }

  /* ----------------------------------------------------------- keyboard -- */

  function onKeyDown(event: KeyboardEvent): void {
    if (destroyed || event.defaultPrevented || event.repeat) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTextEntry(event.target)) return;

    const pile = pileFromKey(event);
    if (pile !== null) {
      if (canPlay()) {
        event.preventDefault();
        handlers.onPlayPile(pile);
      }
      return;
    }

    switch (event.key) {
      case 'u':
      case 'U': {
        if (screen === 'game' && !paused && snapshot?.canUndo === true) {
          event.preventDefault();
          handlers.onUndo();
        }
        return;
      }
      case ' ': {
        // A focused button owns its own Space press.
        if (isButton(event.target)) return;
        event.preventDefault();
        if (screen === 'start') beginRound(false);
        else if (screen === 'game') togglePause();
        else if (overScreen.animating) overScreen.skip();
        else beginRound(true);
        return;
      }
      case 'Enter': {
        if (isButton(event.target)) return;
        if (screen === 'start') {
          event.preventDefault();
          beginRound(false);
        } else if (screen === 'over' && !overScreen.animating) {
          event.preventDefault();
          beginRound(true);
        }
        return;
      }
      case 'Escape': {
        if (screen === 'game') {
          event.preventDefault();
          togglePause();
        } else if (screen === 'over' && overScreen.animating) {
          event.preventDefault();
          overScreen.skip();
        }
        return;
      }
      default:
        return;
    }
  }

  window.addEventListener('keydown', onKeyDown);

  /* ---------------------------------------------------------------- bus -- */

  const offs: Array<() => void> = [];

  if (options.autoBind ?? true) {
    offs.push(
      eventBus.on('game:start', (event) => {
        // The round brings its own clock and stock (a ranked round may open with a stacked deck in a test).
        hud.reset({
          roundSeconds: event.snapshot.secondsRemaining,
          totalCards: event.snapshot.stock.length,
        });
        showGame();
        sync(event.snapshot);
      }),
      eventBus.on('card:play', (event) => sync(event.snapshot)),
      eventBus.on('pile:resolve', (event) => {
        sync(event.snapshot);
        const line = describe(event.resolution);
        if (line !== null) announce(line);
      }),
      eventBus.on('score:change', (event) => {
        hud.flashDelta(event.delta);
        sync(event.snapshot);
      }),
      // The pill itself is updated by `sync`, off the snapshot every event
      // carries. This binding exists only to say it out loud once — a level
      // announced from the snapshot would be re-announced by every subsequent
      // event that happened to carry the same number.
      eventBus.on('level:up', (event) => {
        sync(event.snapshot);
        announce(`Level ${event.level}.`);
      }),
      eventBus.on('bust', (event) => sync(event.snapshot)),
      eventBus.on('tick', (event) => {
        snapshot = event.snapshot;
        hud.setTime(event.secondsRemaining);
      }),
      eventBus.on('undo', (event) => {
        sync(event.snapshot);
        announce('Play taken back.');
      }),
      ...((options.autoGameOver ?? true)
        ? [eventBus.on('game:over', (event) => showGameOver(event.final))]
        : []),
    );
  }

  if (options.showStart ?? true) showStart();
  else showGame();

  /* ------------------------------------------------------------- handle -- */

  /**
   * A capture seam for the frame harness, and nothing else calls it.
   *
   * The alternative was photographing real notifications, which needs two
   * accounts, two rounds and three minutes of clock to produce a settlement —
   * none of which changes a single pixel of what is being reviewed. The same
   * bargain `holdCeremonyAt` makes on the XP frames: the harness drives the
   * state, the app draws it exactly as it always would.
   */
  (window as unknown as { __w21notify?: unknown }).__w21notify = {
    toast: (n: Parameters<typeof notify.toasts.show>[0]) => {
      notify.toasts.hold();
      notify.toasts.show(n);
    },
    unread: (count: number) => {
      for (const bar of everyTopBar()) bar.setUnread(count);
    },
    inbox: (items: Parameters<typeof notify.inbox.open>[0]) => notify.inbox.open(items),
    prompt: (variant: string, view?: unknown) => {
      // The screen has to be ACTIVE, not merely selected: `setScreen` hides
      // the others but each panel carries its own `data-active`, and a frame
      // of a display:none card is a frame of nothing.
      //
      // And it needs a CHALLENGE. Forcing the screen without one produced a
      // page with an empty code box and no stake — a frame of the prompt on a
      // page that cannot exist, which a reviewer rightly queried.
      setScreen('challenge');
      if (view) showChallenge(view as ChallengeView);
      challengeScreenUI.root.dataset.active = 'true';
      challengeScreenUI.showPushPrompt(variant);
    },
    settings: () => {
      pauseScreen.show();
    },
  };

  const ui: UI = {
    notify,
    root,
    hud,
    get screen(): UIScreen {
      return screen;
    },
    get paused(): boolean {
      return paused;
    },
    get revealing(): boolean {
      return overScreen.animating;
    },
    showStart,
    showGame,
    showGameOver,
    /** Say how the round went against whoever else played the deck. */
    // The results screen is ABOUT the challenge it settles: its own toast is
    // read silently rather than fired over the tray (Blockari's `setViewing`).
    // It is also where the round is over for the inbox: what was held back
    // during play lands on the tray, as Blockari flushes onto its results —
    // less the settlement of this very challenge, which the plate is.
    setEarnedChests: (chests) => {
      // While the feature is off there is nothing to earn: the tray's
      // CONTINUE is just CONTINUE, and it closes.
      earned = CHESTS_ENABLED ? chests : [];
      // Blockari's hand-off (state/arrival.ts `resultsArrivalDue`): with a
      // rank-up, the chests arrive straight on its CONTINUE; otherwise once the
      // XP beat has settled and held.
      const rank = earned.some((c) => c.source === 'rank_up');
      overScreen.ceremonyContinueLabel = undefined;
      overScreen.onCeremonyDone =
        earned.length > 0 || devArrival() !== null
          ? () => {
              const list = earned;
              if (rank) chestsArriveAfterResults(list, devArrival());
              else setTimeout(() => chestsArriveAfterResults(list, devArrival()), RESULTS_ARRIVAL_HOLD_MS);
            }
          : undefined;
    },
    showChest: (roll) => {
      if (!CHESTS_ENABLED) return;
      earned = earned.filter((c) => c.id !== roll.id);
      reveal?.destroy();
      const stage: ChestStage = new ChestStage({
        chests: [{ ...roll, roll }],
        arrival: false,
        dev: false,
        quality: chestQuality(),
        onBalance: onChestBalance,
        onFinish: () => {
          stage.destroy();
          if (reveal === stage) reveal = null;
          // Blockari's route: the next unopened chest, if any, plays next.
          void refreshChests().then(() => openNextChest());
        },
      });
      reveal = stage;
    },
    setVerdict: (verdict, options) => {
      if (screen === 'over') {
        if (options?.challengeId) currentChallengeId = options.challengeId;
        notify.setContext({ inRound: false, challengeId: options?.challengeId ?? null });
      }
      overScreen.setVerdict(verdict, options);
      // A win that paid this player: the coins leave the `+N $CHAIN` figure and land in the balance (the results
      // screen's own pill, which has no top bar). Once per challenge; nothing at all on a loss or a draw.
      if (screen === 'over' && verdict && verdict.outcome === 'win' && (verdict.payout ?? 0) > 0 && options?.challengeId) {
        const id = options.challengeId;
        const payout = verdict.payout ?? 0;
        afterPaint(() => {
          void money.flights.win({
            id: `win:${id}`,
            payout,
            pot: payout,
            figure: overScreen.root.querySelector('.w21-rp-stake'),
            toResultsPill: true,
          });
        });
      }
    },
    beginStake: () => money.flights.beginStake(),
    potCoin: () => document.querySelector('.w21-screen[data-active="true"] .w21-pot .w21-coin'),
    setQualityTier: (tier) => {
      flightTier = tier;
    },
    setChallengeNotice: (text) => challengeScreenUI.setNotice(text),
    setShareBusy: (busy) => overScreen.setShareBusy(busy),
    setChallengeShareBusy: (busy) => challengeScreenUI.setShareBusy(busy),
    focusProfileChallenge: () => profileScreenUI.focusChallenge(),
    /** Tell the player what the network is doing, on the start screen. */
    setStartStatus: startScreen.setStatus.bind(startScreen),
    setStartBusy: startScreen.setBusy.bind(startScreen),
    setStartInvite: startScreen.setInvite.bind(startScreen),
    setTarget: startScreen.setTarget.bind(startScreen),
    // Every screen carries its own bar (each is its own scroll container), so
    // the account has to reach all of them or one goes stale the moment the
    // balance moves.
    setAccount: (account: Parameters<StartScreen['setAccount']>[0]) => {
      shownAccount = account;
      updateDock();
      // XP is only ever earned by playing a round: whoever has some has been through the rules, whatever
      // this browser remembers (storage cleared, another browser, a link that dealt them straight in).
      if (account && 'xp' in account && (account as { xp: number }).xp > 0) rememberGuide();
      // The ring wants a fraction, and the account carries the two thresholds
      // that bracket it — computed once here rather than in each of the five
      // bars, which would be five chances to bracket it differently.
      if (account && 'xp' in account) {
        const a = account as { xp: number; prevThreshold: number; nextThreshold: number };
        const span = Math.max(1, a.nextThreshold - a.prevThreshold);
        (account as { xpProgress?: number }).xpProgress = Math.min(
          1,
          Math.max(0, (a.xp - a.prevThreshold) / span),
        );
      }
      // The truth goes to the display; the bars get what the display SHOWS (the held number, mid-flight).
      if (account && typeof account.balance === 'number') money.display.setTruth(account.balance);
      const shown =
        account && money.display.shown !== null ? { ...account, balance: money.display.shown } : account;
      startScreen.setAccount(shown);
      overScreen.setBalance(shown ? shown.balance : null);
      // Create and Take judge affordability by the same number the bars show (the held one mid-flight), and know
      // nothing until there is an account: unknown is not zero.
      createScreenUI.setBalance(shown ? shown.balance : null);
      takeScreenUI.setBalance(shown ? shown.balance : null);
      createScreenUI.topBar.setAccount(shown);
      profileScreenUI.topBar.setAccount(shown);
      takeScreenUI.topBar.setAccount(shown);
      challengeScreenUI.topBar.setAccount(shown);
      historyScreen.topBar.setAccount(shown);
      leaderboardScreen.topBar.setAccount(shown);
      howScreen.topBar.setAccount(shown);
      settingsScreenUI.topBar.setAccount(shown);
      leaderboardScreen.setAccount(account?.username ?? null);
      // The sign-up moment reads who is looking: a guest's rank and chests are what registering keeps.
      const guest = (account as { isGuest?: boolean } | null)?.isGuest === true;
      viewerStore.setState({
        guest,
        passwordless: !guest && (account as { hasPassword?: boolean } | null)?.hasPassword === false,
        username: account?.username ?? '',
        xp: (account as { xp?: number } | null)?.xp ?? 0,
        rank: account?.rank ?? null,
      });
      overScreen.ceremonyKeep = guest ? () => openSignup('rank') : undefined;
      // A row's own badge is the viewer's ACCOUNT rank; the row itself only
      // carries a match level, which is not a rank at all.
      challengeScreenUI.setViewer(account?.username ?? null);
    },
    showDailyGrant: (amount: number) => {
      startScreen.showDailyGrant(amount);
      createScreenUI.topBar.showDailyGrant(amount);
    },
    showXp: (gained) => overScreen.showXp(gained),
    setRecent: (rows) => {
      startScreen.setRecent(rows);
      profileScreenUI.setRecent(rows);
    },
    showHistory,
    refreshHistory,
    refreshProfileRecent,
    refreshHomeRecent,
    refreshProfileStats,
    profileIsOwn: () => screen === 'profile' && profileScreenUI.showingOwn,
    showLeaderboard,
    showHowItWorks,
    showSettings,
    showCreate,
    setChallengeCounts: (counts) => {
      createScreenUI.setCounts(counts);
      takeScreenUI.setCounts(counts);
    },
    setTickets: (tickets) => {
      createScreenUI.setTickets(tickets);
      takeScreenUI.setTickets(tickets);
    },
    showBestScores: (rows) => openBestScores(rows),
    showTake,
    setQueue: (rows) => takeScreenUI.setQueue(rows),
    setQueues: (queues) => takeScreenUI.setQueues(queues),
    takeFee: () => takeScreenUI.fee,
    takeCustom: () => takeScreenUI.customFilter,
    takeBusy: () => takeScreenUI.busy,
    prefillTake: (code) => takeScreenUI.prefill(code),
    showChallenge,
    showProfile,
    openProfile,
    failProfile: (message: string) => profileScreenUI.fail(message),
    failCreate: (message: string) => createScreenUI.fail(message),
    failTake: (message: string) => takeScreenUI.fail(message),
    setGuideCaption: (text: string) => guide.setCaption(text),
    endGuide: () => finishGuide(),
    guideInset: () => guide.inset(),
    compactGuide: () => guide.compact(),
    sync,
    setPaused,
    togglePause,
    announce,
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('keydown', onKeyDown);
      for (const off of offs) off();
      offs.length = 0;
      zoomGate.destroy();
      zoomHint.destroy();
      hud.destroy();
      guide.destroy();
      startScreen.destroy();
      pauseScreen.destroy();
      overScreen.destroy();
      activeWatch.disconnect();
      screensLayer.remove();
      liveRegion.remove();
      root.classList.remove('w21');
      delete root.dataset.screen;
      delete root.dataset.paused;
      clearTokens(tokenTarget);
    },
  };

  return ui;
}
